import C1_EloItem from "./C1_EloItem";
import { C1_EloGroup } from "./C1_EloGroup";
import { C1_Furniture } from "./C1_Furniture";
import { C1_Opening } from "./C1_Opening";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { eloRootAndOwner } from "./C1_eloOwnerEditing";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import type { Rect } from "../types/C1_AppTypes";
import { furnitureBounds } from "../lib/C1_furnitureBounds";
import { getEloItemProfile } from "../data/C1_eloItemsRegistry";
import { moveInteractiveEloPlacement } from "./C1_interactiveEloPlacement";
import { generateTags } from "../estada/C1_generateTags";
import { c1_state } from "./C1_runtimeContext";

export function distanceOutsideBounds(point: {x: number; y: number}, bounds: Rect): number {
  return Math.hypot(Math.max(bounds.x1 - point.x, 0, point.x - bounds.x2),
    Math.max(bounds.y1 - point.y, 0, point.y - bounds.y2));
}

function ownerBounds(owner: C1_Furniture | C1_Opening): Rect | undefined {
  if (owner instanceof C1_Furniture) {
    const origin = owner.equipmentPostitionGeometric(owner.id);
    if (!origin) return;
    const xRatio = (owner.definition?.originXPercentage ?? 0) / 100;
    return furnitureBounds(origin.x, origin.y, owner.baseProps.width, owner.baseProps.height,
      owner.absoluteRotation, owner.absoluteReflect ? 1 - xRatio : xRatio,
      (owner.definition?.originYPercentage ?? 0) / 100);
  }
  const room = owner.room;
  if (!room) return;
  const center = owner.absolutePosition;
  const wall = owner.wall ? room.wallPoints(owner.wall) : undefined;
  const rotation = wall ? Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * 180 / Math.PI
    : {top: 0, right: 90, bottom: 180, left: 270}[owner.side];
  const thickness = owner.wall && !room.isExteriorSegment(owner.wall)
    ? owner.project.settings.wallThicknessInterior : owner.project.settings.wallThicknessExterior;
  return furnitureBounds(center.x, center.y, owner.width, thickness, rotation, 0.5, 0.5);
}

function standalonePalette(item: C1_EloItem): string | undefined {
  switch (item.definition?.type) {
    case C1_EloItem_Type.Button: return "electricalButton";
    case C1_EloItem_Type.Light: return "electricalLight";
    case C1_EloItem_Type.Plug:
    case C1_EloItem_Type.PlugGroup: return "electricalOutlet";
    case C1_EloItem_Type.Camera: return "electricalCamera";
    case C1_EloItem_Type.IntermediateDistribution: return "electricalDistribution";
    case C1_EloItem_Type.WindowContact: return "electricalWindowContact";
    case C1_EloItem_Type.Jalousie: return "electricalJalousie";
    case C1_EloItem_Type.Blind: return "electricalBlind";
    case C1_EloItem_Type.Sensor: return "electricalSensor";
  }
}

function rebindChildren(item: C1_EloItem, definition: C1_DefinitionItem): void {
  if (!(item instanceof C1_EloGroup)) return;
  for (const child of item.items) {
    const replacement = definition.children.findFirst(candidate => candidate.definitionId === child.definitionId);
    if (!replacement) continue;
    child.definition = replacement;
    rebindChildren(child, replacement);
  }
}

/** Called on drop so changing the rendered owner does not interrupt pointer capture. */
export function detachEloItemOutsideOwner(item: C1_EloItem, point: {x: number; y: number}, disabled = false): boolean {
  if (item.interactivCreated) return false;
  const owner = eloRootAndOwner(item)?.owner;
  if (!(owner instanceof C1_Furniture) && !(owner instanceof C1_Opening)) return false;
  const room = owner.room;
  const bounds = ownerBounds(owner);
  if (!room || !bounds || distanceOutsideBounds(point, bounds) <= 0.5) return false;
  const paletteId = standalonePalette(item);
  const sourceId = paletteId ? getEloItemProfile(paletteId)[0]?.id : item.definitionId;
  const definitionPath = paletteId ? paletteId + "/" + sourceId +
    (item.definition?.type === C1_EloItem_Type.Plug ? "/plug.generic" : "") : item._defId;
  const definition = state.objectRegistry.getObject<C1_DefinitionItem>(definitionPath);
  if (!definition) return false;
  const placement = moveInteractiveEloPlacement(
    {paletteId: paletteId ?? sourceId ?? "", relativePosition: {x: 0, y: 0}, rotation: 0},
    point, room.innerRect(), [], disabled, room.placementGeometry(),
  );
  if (!item.adoptInteractiveRoom(room, definition, placement)) return false;
  rebindChildren(item, definition);
  generateTags(c1_state.project);
  return true;
}
